import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import Modal from '../components/Modal';

const TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'exam', label: 'Экзамен' },
  { value: 'grade', label: 'Класс' },
  { value: 'topic', label: 'Тема' },
  { value: 'difficulty', label: 'Сложность' },
  { value: 'year', label: 'Год' },
  { value: 'collection', label: 'Подборка' },
  { value: 'status', label: 'Статус' },
];

const TYPE_LABELS = Object.fromEntries(
  TYPE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
);

function aliasesToText(arr) {
  if (!Array.isArray(arr)) return '';
  return arr.join('\n');
}

function textToAliases(text) {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

const emptyForm = {
  slug: '',
  name: '',
  type: 'collection',
  parent: '',
  description: '',
  aliasesText: '',
  is_active: true,
};

export default function TeacherTagsPage() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(100);
  const [filters, setFilters] = useState({
    type: '',
    search: '',
    activeOnly: false,
  });

  const [parentOptions, setParentOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [originalSlug, setOriginalSlug] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (filters.type) params.set('type', filters.type);
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.activeOnly) params.set('active_only', '1');
      const res = await api.get(`/tasks/editor/tags/manage/?${params.toString()}`);
      const data = res.data;
      if (Array.isArray(data?.results)) {
        setTags(data.results);
        setTotalCount(data.count ?? data.results.length);
      } else if (Array.isArray(data)) {
        setTags(data);
        setTotalCount(data.length);
      } else {
        setTags([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(
        e.response?.status === 403
          ? 'Нет прав для управления тегами'
          : 'Не удалось загрузить теги'
      );
      setTags([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters.type, filters.search, filters.activeOnly]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/editor/tags/manage/?page_size=500');
        const data = res.data;
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        if (!cancelled) {
          setParentOptions(list.map((t) => ({ id: t.id, slug: t.slug, name: t.name })));
        }
      } catch {
        if (!cancelled) setParentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  function openCreate() {
    setModalMode('create');
    setEditingId(null);
    setOriginalSlug(null);
    setForm({ ...emptyForm });
    setFormError('');
    setModalOpen(true);
  }

  function openEdit(tag) {
    setModalMode('edit');
    setEditingId(tag.id);
    setOriginalSlug(tag.slug);
    setForm({
      slug: tag.slug,
      name: tag.name,
      type: tag.type,
      parent: tag.parent ? String(tag.parent.id) : '',
      description: tag.description || '',
      aliasesText: aliasesToText(tag.aliases),
      is_active: tag.is_active,
    });
    setFormError('');
    setModalOpen(true);
  }

  function closeModal() {
    if (saving) return;
    setModalOpen(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const payload = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      type: form.type,
      description: form.description.trim(),
      aliases: textToAliases(form.aliasesText),
      is_active: form.is_active,
    };
    if (form.parent) {
      payload.parent = Number(form.parent);
    } else {
      payload.parent = null;
    }
    try {
      if (modalMode === 'create') {
        await api.post('/tasks/editor/tags/manage/', payload);
      } else {
        await api.patch(`/tasks/editor/tags/manage/${encodeURIComponent(originalSlug)}/`, payload);
      }
      setModalOpen(false);
      await fetchTags();
      const res = await api.get('/tasks/editor/tags/manage/?page_size=500');
      const data = res.data;
      const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setParentOptions(list.map((t) => ({ id: t.id, slug: t.slug, name: t.name })));
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === 'object') {
        const first = Object.entries(d)[0];
        setFormError(
          first
            ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`
            : 'Ошибка сохранения'
        );
      } else {
        setFormError('Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(tag) {
    try {
      await api.patch(`/tasks/editor/tags/manage/${encodeURIComponent(tag.slug)}/`, {
        is_active: !tag.is_active,
      });
      await fetchTags();
    } catch {
      setError('Не удалось изменить статус тега');
    }
  }

  return (
    <div className="teacher-tags-page">
      <div className="teacher-tags-header">
        <Link to="/teacher" className="back-link">
          ← Кабинет преподавателя
        </Link>
        <h1>Все теги</h1>
        <p className="teacher-tags-lead">
          Справочник тегов задач. Чтобы скрыть тег из каталога, отключите «Активен» — привязки к задачам
          сохраняются.
        </p>
        <div className="teacher-tags-toolbar">
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
            Новый тег
          </button>
        </div>
      </div>

      <div className="teacher-tags-filters form-row-inline">
        <select
          className="form-select"
          value={filters.type}
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, type: e.target.value }));
          }}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="form-input"
          placeholder="Поиск по названию или slug"
          value={filters.search}
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, search: e.target.value }));
          }}
        />
        <label className="editor-filter-check">
          <input
            type="checkbox"
            checked={filters.activeOnly}
            onChange={(e) => {
              setPage(1);
              setFilters((f) => ({ ...f, activeOnly: e.target.checked }));
            }}
          />
          Только активные
        </label>
      </div>

      {error && <div className="form-error-banner">{error}</div>}

      {loading ? (
        <div className="loading-inline">Загрузка…</div>
      ) : (
        <>
          <div className="teacher-tags-table-wrap">
            <table className="teacher-tags-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Slug</th>
                  <th>Тип</th>
                  <th>Активен</th>
                  <th>Родитель</th>
                  <th>Задач</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tags.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="teacher-tags-empty">
                      Нет тегов по текущим фильтрам.
                    </td>
                  </tr>
                ) : (
                  tags.map((tag) => (
                    <tr key={tag.id} className={!tag.is_active ? 'row-inactive' : ''}>
                      <td>{tag.name}</td>
                      <td>
                        <code>{tag.slug}</code>
                      </td>
                      <td>{TYPE_LABELS[tag.type] || tag.type}</td>
                      <td>{tag.is_active ? 'да' : 'нет'}</td>
                      <td>{tag.parent ? tag.parent.name : '—'}</td>
                      <td>{tag.tasks_count ?? 0}</td>
                      <td className="teacher-tags-actions">
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => openEdit(tag)}
                        >
                          Изменить
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => toggleActive(tag)}
                          title={tag.is_active ? 'Скрыть из каталога' : 'Снова показывать в каталоге'}
                        >
                          {tag.is_active ? 'Выключить' : 'Включить'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="teacher-tags-pagination">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Назад
              </button>
              <span>
                Стр. {page} из {totalPages} ({totalCount} тегов)
              </span>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}

      <Modal
        open={modalOpen}
        title={modalMode === 'create' ? 'Новый тег' : 'Редактирование тега'}
        onClose={closeModal}
      >
        <form className="teacher-tags-form" onSubmit={handleSave}>
          {formError && <div className="form-error-banner">{formError}</div>}
          <div className="editor-field">
            <label>Slug</label>
            <input
              className="form-input"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
              disabled={saving}
            />
          </div>
          <div className="editor-field">
            <label>Название</label>
            <input
              className="form-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              maxLength={100}
              disabled={saving}
            />
          </div>
          <div className="editor-field">
            <label>Тип</label>
            <select
              className="form-select"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              disabled={saving}
            >
              {TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="editor-field">
            <label>Родитель (необязательно)</label>
            <select
              className="form-select"
              value={form.parent}
              onChange={(e) => setForm((f) => ({ ...f, parent: e.target.value }))}
              disabled={saving}
            >
              <option value="">— нет —</option>
              {parentOptions
                .filter((p) => p.id !== editingId)
                .map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} ({p.slug})
                  </option>
                ))}
            </select>
          </div>
          <div className="editor-field">
            <label>Описание</label>
            <textarea
              className="form-input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div className="editor-field">
            <label>Алиасы (по одному на строку)</label>
            <textarea
              className="form-input"
              rows={3}
              value={form.aliasesText}
              onChange={(e) => setForm((f) => ({ ...f, aliasesText: e.target.value }))}
              disabled={saving}
            />
          </div>
          <label className="editor-filter-check">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              disabled={saving}
            />
            Активен (виден в публичном списке тегов)
          </label>
          <div className="teacher-tags-modal-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={closeModal} disabled={saving}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
